import { formatTimeInTz } from "@/design-system/lib/format";
import type { BusyBlockDto } from "@/api/appointments";
import { hourTicks, placeBusyBlock } from "../lib/availability";
import { zoneCaption } from "@/lib/appointmentZones";

/** One person's row on the strip. */
export interface AvailabilityRow {
  userId: string;
  label: string;
  /** Renders a subtle "You" marker so the organizer's own row is obvious. */
  isOrganizer: boolean;
}

interface Props {
  /** The organizer-local day being shown, `YYYY-MM-DD`. */
  dayIso: string;
  tz: string | null | undefined;
  rows: AvailabilityRow[];
  /** Busy blocks per user id. A user absent from the map is treated as free. */
  busyByUser: ReadonlyMap<string, BusyBlockDto[]>;
  loading?: boolean;
}

const TICKS = hourTicks(2);

/**
 * Calendar — attendee availability strip. A compact busy/free timeline for a
 * single day, one row per internal attendee (plus the organizer), so an
 * organizer can see the gaps and pick a time visually instead of guessing after
 * a conflict warning.
 *
 * Privacy: this renders exactly the tier the conflict warning already ships —
 * busy time ranges for everyone, and an appointment title only where the server
 * chose to send one (Appointments.ViewAll holders). There is deliberately NO
 * client-side permission check; a missing title simply renders as a time range.
 *
 * External attendees never appear — rows are driven by internal user ids, and
 * an external attendee has no user id by construction.
 */
export function AttendeeAvailabilityStrip({
  dayIso,
  tz,
  rows,
  busyByUser,
  loading,
}: Props) {
  if (rows.length === 0) return null;

  // 🔴 Null anchor DELIBERATELY, even though this strip does receive a single
  // `dayIso`. D9 names it among the surfaces that span arbitrary dates, and the
  // name-only form is the one that can never be false, so the decision is
  // followed as written rather than "improved" into an offset. Reported in the
  // PR body: if the offset is wanted here, `zoneCaption(tz, dayAnchor)` is the
  // one-line change, and the day is already in scope.
  const caption = zoneCaption(tz, null);

  return (
    <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
      {caption && (
        <p className="text-caption text-gray-mid m-0 mb-space-2">{caption}</p>
      )}
      {/* Hour axis */}
      <div className="flex items-center gap-space-2">
        <div className="w-24 shrink-0" aria-hidden />
        <div className="relative h-4 flex-1">
          {TICKS.map((tick) => (
            <span
              key={tick.hour}
              className="absolute top-0 -translate-x-1/2 text-[10px] text-gray-mid"
              style={{ left: `${tick.leftPct}%` }}
            >
              {tick.label}
            </span>
          ))}
        </div>
      </div>

      {/* One scrolling track per person — scrolls rather than truncates, since
          Team Members is uncapped and hiding someone's availability inside an
          availability widget is the wrong failure mode. */}
      <div className="max-h-40 overflow-y-auto flex flex-col gap-space-1">
        {rows.map((row) => {
          const blocks = busyByUser.get(row.userId) ?? [];
          return (
            <div key={row.userId} className="flex items-center gap-space-2">
              <div className="w-24 shrink-0 truncate text-caption text-onyx" title={row.label}>
                {row.label}
                {row.isOrganizer && (
                  <span className="ml-space-1 text-gold-shadow">(you)</span>
                )}
              </div>
              <div
                className="relative h-5 flex-1 rounded-sm bg-white-pure border border-border-light overflow-hidden"
                role="img"
                aria-label={describeRow(row, blocks, tz)}
              >
                {/* Hour gridlines */}
                {TICKS.slice(1, -1).map((tick) => (
                  <span
                    key={tick.hour}
                    aria-hidden
                    className="absolute top-0 bottom-0 w-px bg-border-light"
                    style={{ left: `${tick.leftPct}%` }}
                  />
                ))}

                {blocks.map((block, i) => {
                  const placed = placeBusyBlock(block, dayIso, tz);
                  if (!placed) return null;
                  return (
                    <span
                      key={`${block.startUtc}-${block.endUtc}-${i}`}
                      title={describeBlock(block, tz)}
                      className="absolute top-0 bottom-0 rounded-sm bg-gold"
                      style={{
                        left: `${placed.leftPct}%`,
                        width: `${placed.widthPct}%`,
                      }}
                    >
                      {block.isAllDay && (
                        <span className="flex h-full items-center justify-center text-[10px] font-display text-onyx">
                          All day
                        </span>
                      )}
                    </span>
                  );
                })}

                {!loading && blocks.length === 0 && (
                  <span className="flex h-full items-center justify-center text-[10px] text-gray-mid">
                    Free
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tooltip for one block. Uses the title when the API supplied one
 * (Appointments.ViewAll holders) and falls back to a busy time-range otherwise
 * — the same rule the conflict badge uses.
 */
function describeBlock(block: BusyBlockDto, tz: string | null | undefined): string {
  if (block.isAllDay) return block.title ?? "Busy all day";
  const range = `${formatTimeInTz(block.startUtc, tz)} – ${formatTimeInTz(block.endUtc, tz)}`;
  return block.title ? `${block.title} (${range})` : `Busy ${range}`;
}

/** Screen-reader summary of a whole row. */
function describeRow(
  row: AvailabilityRow,
  blocks: BusyBlockDto[],
  tz: string | null | undefined,
): string {
  if (blocks.length === 0) return `${row.label}: free all day`;
  return `${row.label}: ${blocks.map((b) => describeBlock(b, tz)).join("; ")}`;
}
